import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { ClassMarkerStatusResponse, classMarkerApi } from '../../../api/classmarker';
import { DefaultRejectValue } from '../../../types';

// Async thunks
export const registerForExamThunk = createAsyncThunk<void, { courseId: string }, { rejectValue: DefaultRejectValue }>(
  'classmarker/registerForExam',
  async ({ courseId }, { rejectWithValue }) => {
    try {
      await classMarkerApi.registerForExam({ courseId });
    } catch (error) {
      return rejectWithValue(error as DefaultRejectValue);
    }
  },
);

export const getTestUrlThunk = createAsyncThunk<
  { testUrl: string },
  { courseId: string },
  { rejectValue: DefaultRejectValue }
>('classmarker/getTestUrl', async ({ courseId }, { rejectWithValue }) => {
  try {
    const response = await classMarkerApi.getTestUrl(courseId);
    return response;
  } catch (error) {
    return rejectWithValue(error as DefaultRejectValue);
  }
});

export const getTestStatusThunk = createAsyncThunk<
  ClassMarkerStatusResponse,
  { courseId: string },
  { rejectValue: DefaultRejectValue }
>('classmarker/getTestStatus', async ({ courseId }, { rejectWithValue }) => {
  try {
    const response = await classMarkerApi.getTestStatus(courseId);
    return response;
  } catch (error) {
    return rejectWithValue(error as DefaultRejectValue);
  }
});

interface ClassMarkerSlice {
  isRegistering: boolean;
  testUrl: string | null;
  testStatus: ClassMarkerStatusResponse | null;
  isLoadingUrl: boolean;
  isLoadingStatus: boolean;
  error: string | null;
}

const initialState: ClassMarkerSlice = {
  isRegistering: false,
  testUrl: null,
  testStatus: null,
  isLoadingUrl: false,
  isLoadingStatus: false,
  error: null,
};

export const classMarkerSlice = createSlice({
  name: 'classmarker',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTestUrl: (state) => {
      state.testUrl = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register for exam
      .addCase(registerForExamThunk.pending, (state) => {
        state.isRegistering = true;
        state.error = null;
      })
      .addCase(registerForExamThunk.fulfilled, (state) => {
        state.isRegistering = false;
      })
      .addCase(registerForExamThunk.rejected, (state, action) => {
        state.isRegistering = false;
        const message = action.payload?.message;
        state.error = Array.isArray(message) ? message[0] : message || 'Failed to register for exam';
      })
      // Get test URL
      .addCase(getTestUrlThunk.pending, (state) => {
        state.isLoadingUrl = true;
        state.error = null;
      })
      .addCase(getTestUrlThunk.fulfilled, (state, action) => {
        state.isLoadingUrl = false;
        state.testUrl = action.payload.testUrl;
      })
      .addCase(getTestUrlThunk.rejected, (state, action) => {
        state.isLoadingUrl = false;
        const message = action.payload?.message;
        state.error = Array.isArray(message) ? message[0] : message || 'Failed to get test URL';
      })
      // Get test status
      .addCase(getTestStatusThunk.pending, (state) => {
        state.isLoadingStatus = true;
        state.error = null;
      })
      .addCase(getTestStatusThunk.fulfilled, (state, action) => {
        state.isLoadingStatus = false;
        state.testStatus = action.payload;
      })
      .addCase(getTestStatusThunk.rejected, (state, action) => {
        state.isLoadingStatus = false;
        const message = action.payload?.message;
        state.error = Array.isArray(message) ? message[0] : message || 'Failed to get test status';
      });
  },
});

export const { clearError, clearTestUrl } = classMarkerSlice.actions;

export const actions = {
  registerForExam: registerForExamThunk,
  getTestUrl: getTestUrlThunk,
  getTestStatus: getTestStatusThunk,
  ...classMarkerSlice.actions,
};

export const { reducer } = classMarkerSlice;
